import Checkbox from "@/v2-ui/checkbox"
import Label from "@/v2-ui/label"
import cn from "@/v2-ui/utils/utils.cn"
import LicenseFeatureGroupDetailsHoverCard from "@/v2-backoffice-client/license/featureGroup/compose/LicenseFeatureGroupDetailsHoverCard"
import LicenseFeatureGroupIncludedMark from "@/v2-backoffice-client/license/featureGroup/compose/LicenseFeatureGroupIncludedMark"
import LicenseFeatureGroupPackageOnlyMark from "@/v2-backoffice-client/license/featureGroup/compose/LicenseFeatureGroupPackageOnlyMark"
import LicenseFeatureGroupReachIndicator from "@/v2-backoffice-client/license/featureGroup/compose/LicenseFeatureGroupReachIndicator"
import LicenseFeatureGroupReachMark from "@/v2-backoffice-client/license/featureGroup/compose/LicenseFeatureGroupReachMark"
import LicenseFeatureGroupTickCheckbox from "@/v2-backoffice-client/license/featureGroup/compose/LicenseFeatureGroupTickCheckbox"
import {
  PENDING_CHANGE_LABEL,
  ROW_NOTE_CLASS,
} from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.compose.constants"
import { describeCheckedBy } from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.checkedBy.utils"
import {
  isPersonLens,
  useFeatureGroupLens,
} from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.lens.context"
import {
  describePackagesFact,
  describeWhoGetsIt,
  tickMeaningOf,
} from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.placement.utils"
import {
  describeIncluded,
  describeReach,
  describeRowStatus,
  isFixedByLens,
  rowStatusOf,
} from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.lens.utils"
import type {
  FeatureGroupPickedLike,
  FeatureGroupRuleLike,
} from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.compose.utils"
import {
  categoriesOf,
  countriesOf,
  grantOf,
  isLockedContent,
  packagesOf,
  toPickedContent,
  type LicenseFeatureGroupContentGrant,
  type LicenseFeatureGroupContentItem as ContentItem,
} from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.content.utils"

export type LicenseFeatureGroupContentItemProps = {
  item: ContentItem
  /** Two items in the group share a name: show the code so they can be told apart. */
  showsCode?: boolean
  /** The class as the operator reads it ("map layer"), for the hover card. */
  noun: string
  /** Category code → operator label, for layers filed in several. */
  categoryLabelOf(code: string): string
  rules: readonly FeatureGroupRuleLike[]
  persistedRules: readonly FeatureGroupRuleLike[]
  /** The package being edited, so "also in" never names it. */
  featureGroupName: string
  isPicked(code: string): boolean
  wasPicked(code: string): boolean
  onSetPicked(
    features: readonly FeatureGroupPickedLike[],
    picked: boolean,
  ): void
}

/** What the rule that ticks the row covers, as the row says it. */
function ruleNounOf(
  grant: LicenseFeatureGroupContentGrant | null,
  noun: string,
) {
  return grant === "category" ? "whole category" : `all ${noun}s`
}

// One base map or map layer as a checklist row. Ticked by hand, or shown
// ticked and still because a rule already reaches it ("all map layers",
// "whole category") — the row says which, so an operator never wonders why
// a box will not untick. Only a package-only item depends on packages; the
// badge marks those through the package lens, and the hover card spells out
// what the grant does for a user, where the item is offered, and who else
// carries it. Through the customer lens the row is the answer for that
// customer: a fixed tick where the product or a license includes it, a
// checkbox where this tab decides.
function LicenseFeatureGroupContentItem(
  props: LicenseFeatureGroupContentItemProps,
) {
  const {
    item,
    showsCode = false,
    noun,
    categoryLabelOf,
    rules,
    persistedRules,
    featureGroupName,
    isPicked,
    wasPicked,
    onSetPicked,
  } = props

  const grant = grantOf(item, rules, isPicked)
  const wasGranted = grantOf(item, persistedRules, wasPicked) !== null
  const isGranted = grant !== null
  const isChanged = isGranted !== wasGranted
  const isByRule = grant === "all" || grant === "category"
  const lens = useFeatureGroupLens()
  const status = rowStatusOf(
    lens,
    item,
    isByRule ? "rule" : grant === "pick" ? "pick" : null,
  )
  const isFixed = isFixedByLens(status)
  const statusText =
    status && describeRowStatus(status, ruleNounOf(grant, noun))
  const reach =
    lens.kind === "customer" && lens.reaches
      ? lens.reaches(item.Code)
      : undefined
  const countries = countriesOf(item)
  const categories = categoriesOf(item).map(categoryLabelOf)
  const otherPackages =
    lens.kind === "package"
      ? packagesOf(item).filter((name) => name !== featureGroupName)
      : []
  // The checkbox carries what the tick means on a free item (a dash: a
  // placement, not access); see license.featureGroup.placement.utils.
  const tickMeaning = tickMeaningOf({
    isPackageOnly: isLockedContent(item),
    isCarriedHere: isGranted,
    packageNames: packagesOf(item),
  })

  return (
    <li
      className={cn(
        "min-w-0 border-l-2 border-l-transparent",
        isChanged && "border-l-primary bg-primary/5",
      )}
    >
      <Label
        className={cn(
          "flex items-center gap-2.5 py-1 pl-7 pr-2 font-normal",
          isByRule || isFixed ? "cursor-default" : "cursor-pointer",
        )}
      >
        {isPersonLens(lens) ? (
          <LicenseFeatureGroupReachIndicator
            state={reach ? "all" : "none"}
            title={describeReach({
              reaches: reach === true,
              status,
              userName: lens.userName,
              customerName: lens.customerName,
            })}
          />
        ) : isFixed && status && lens.kind === "customer" ? (
          <LicenseFeatureGroupIncludedMark
            title={describeIncluded(status, lens.customerName)}
          />
        ) : lens.kind === "package" ? (
          <LicenseFeatureGroupTickCheckbox
            meaning={tickMeaning}
            isCarriedHere={isGranted}
            disabled={isByRule}
            packageName={featureGroupName}
            rowName={item.Name}
            otherPackages={otherPackages}
            ariaLabel={`Grant ${item.Name}`}
            onToggle={(nextCarried) => {
              onSetPicked([toPickedContent(item)], nextCarried)
            }}
          />
        ) : (
          <Checkbox
            checked={isGranted}
            disabled={isByRule}
            aria-label={`Add ${item.Name} for ${lens.customerName}`}
            onCheckedChange={(checked) => {
              onSetPicked([toPickedContent(item)], checked === true)
            }}
          />
        )}
        <LicenseFeatureGroupDetailsHoverCard
          title={item.Name}
          code={item.Code}
          description={item.Description}
          facts={[
            { label: "Kind", value: noun },
            ...(countries.length > 0
              ? [{ label: "Offered in", value: countries.join(", ") }]
              : [
                  { label: "Offered in", value: "no country — nobody sees it" },
                ]),
            ...(categories.length > 0
              ? [{ label: "Categories", value: categories.join(", ") }]
              : []),
            {
              label: "Checked by",
              value: describeCheckedBy({ kind: "content" }),
            },
            {
              label: "Who gets it",
              value: describeWhoGetsIt(isLockedContent(item)),
            },
            ...(item.IsEnabled
              ? []
              : [
                  {
                    label: "Published",
                    value:
                      "not yet — no user sees it until the review queue enables it",
                  },
                ]),
            describePackagesFact(isLockedContent(item), packagesOf(item)),
          ]}
        >
          <span className="min-w-[5rem] flex-1 truncate text-xs">
            {item.Name}
            {showsCode && (
              <span className="ml-1.5 font-mono text-[11px] text-muted-foreground">
                {item.Code}
              </span>
            )}
          </span>
        </LicenseFeatureGroupDetailsHoverCard>
        {!item.IsEnabled && (
          <span className={ROW_NOTE_CLASS}>not published</span>
        )}
        {countries.length > 0 && (
          <span className={ROW_NOTE_CLASS}>{countries.join(", ")}</span>
        )}
        {lens.kind === "package" && isByRule && (
          <span className={ROW_NOTE_CLASS}>{ruleNounOf(grant, noun)}</span>
        )}
        {statusText && (
          <span className={ROW_NOTE_CLASS} title={statusText}>
            {statusText}
          </span>
        )}
        {isChanged && (
          <span className="shrink-0 text-[11px] text-primary">
            {isGranted
              ? PENDING_CHANGE_LABEL.added
              : PENDING_CHANGE_LABEL.removed}
          </span>
        )}
        {lens.kind === "package" && isLockedContent(item) && (
          <LicenseFeatureGroupPackageOnlyMark />
        )}
        {otherPackages.length > 0 && (
          <span
            className={ROW_NOTE_CLASS}
            title={`Also in ${otherPackages.join(", ")}`}
          >
            also in {otherPackages.join(", ")}
          </span>
        )}
        {reach !== undefined &&
          lens.kind === "customer" &&
          !lens.isReadOnly && (
            <LicenseFeatureGroupReachMark
              reaches={reach}
              userName={lens.userName}
            />
          )}
      </Label>
    </li>
  )
}

export default LicenseFeatureGroupContentItem
